import { Session } from '@services/session';
import $ from 'jquery';

import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  HostListener,
  ChangeDetectionStrategy,
  AfterViewInit,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { QuickActionsModalService } from '@services/quick-actions-modal.service';

import { KEY_CODES } from '@lib/key-codes';

import { QUICK_ACTIONS, QUICK_ACTIONS_ICONS } from './quick-action.constants';

export interface IQuickAction {
  link: (session: Session) => string;
  label: string;
  name: string;
  shouldShowQuickAction: (session: Session) => boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'quick-action-modal',
  templateUrl: './quick-action.component.html',
  styleUrls: ['./quick-action.component.scss'],
})
export class QuickActionModalComponent implements OnInit, AfterViewInit, OnDestroy {
  public actionItems: IQuickAction[];
  public icons: {[key: string]: string};
  private selectedActionIndex: number;
  private $el;
  private $modal;

  public get hasQuickActions(): boolean {
    return this.actionItems && !!this.actionItems.length;
  }

  constructor(
    private elRef: ElementRef,
    private router: Router,
    private sanitizer: DomSanitizer,
    private quickActionsService: QuickActionsModalService,
    private session: Session,
  ) {
    this.$el = $(this.elRef.nativeElement);
  }

  public ngOnInit() {
    this.actionItems = QUICK_ACTIONS.filter((action: IQuickAction) => action.shouldShowQuickAction(this.session));
    this.icons = QUICK_ACTIONS_ICONS;
    this.setDefaultSelectedAction();
    this.$modal = this.$el.find('.ui.small.modal').modal({
      onHide: this.setDefaultSelectedAction.bind(this),
    });
    this.quickActionsService.registerComponent(this);
  }

  public ngAfterViewInit() {
    let element: HTMLElement;
    this.actionItems.forEach(item => {
      element = document.querySelector(`#icon-${item.name}`);
      element.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML,
        this.sanitizer.bypassSecurityTrustHtml(this.icons[item.name]));
    });
  }

  public ngOnDestroy() {
    this.$modal.modal('hide');
    this.$modal.modal('destroy');
    this.$modal.remove();
    this.quickActionsService.unRegisterComponent();
  }

  public show(): void {
    this.$modal.modal('show');
  }

  public close(): void {
    this.$modal.modal('hide');
  }

  public navigate(): void {
    this.router.navigateByUrl(this.actionItems[this.selectedActionIndex].link(this.session));
    this.$modal.modal('hide');
  }

  public toggleSelectedAction(event: MouseEvent, item: IQuickAction, index: number): void {
    if ((event.type === 'mouseenter' && !this.isActionSelected(index))
      || (event.type === 'mouseleave' && this.isActionSelected(index))) {
      this.selectedActionIndex = index;
    }
  }

  public selectPreviousAction(event: KeyboardEvent): void {
    if (this.selectedActionIndex > 0) {
      event.preventDefault();
      this.selectedActionIndex -= 1;
    }
  }

  public selectNextAction(event: KeyboardEvent): void {
    if (this.selectedActionIndex < this.actionItems.length - 1) {
      event.preventDefault();
      this.selectedActionIndex += 1;
    }
  }

  public isActionSelected(index: number): boolean {
    return index === this.selectedActionIndex;
  }

  private setDefaultSelectedAction(): void {
    this.selectedActionIndex = 0;
  }

  private isAnyElementInFocus(): boolean {
    const inputInFocus: HTMLInputElement = $('input:focus')[0] || $('textarea:focus')[0];
    return inputInFocus && !inputInFocus.readOnly;
  }

  @HostListener('document:keydown', ['$event'])
  private handleKeyboardShortcuts(event: KeyboardEvent): void {
    if (!this.isAnyElementInFocus() && event.keyCode === KEY_CODES.ForwardSlash) {
      event.preventDefault();
      this.show();
    }
  }
}
